<template>
  <div class="be-policy" v-if="hasAnyPolicy">
    <h2 class="be-policy__title mb-4">Accommodation Policy</h2>

    <div class="row be-policy__section" v-if="hasCheckInSection">
      <div class="col-12 col-lg-3 mb-3 mb-lg-0">
        <h3 class="be-policy__label mb-0">Check-in Procedure</h3>
      </div>
      <div class="col-12 col-lg-9">
        <div class="be-policy__time-row" v-if="hasCheckTimes">
          <div class="be-policy__time">
            <p class="be-policy__time-label mb-1">Check In:</p>
            <p class="be-policy__time-value mb-0">{{ checkInTime }}</p>
          </div>
          <div class="be-policy__time">
            <p class="be-policy__time-label mb-1">Check Out:</p>
            <p class="be-policy__time-value mb-0">{{ checkOutTime }}</p>
          </div>
        </div>
        <div
          v-if="hasCheckInPolicy"
          class="be-policy__content"
          :class="{ 'mt-4': hasCheckTimes }"
          v-html="sanitizePolicyHtml(getMerchant.check_in_policy)"></div>
      </div>
    </div>

    <div class="row" v-if="hasCheckInSection && hasPolicyCustom">
      <div class="col-12 col-lg-9 offset-lg-3">
        <hr class="be-policy__divider" />
      </div>
    </div>

    <div class="row be-policy__section" v-if="hasPolicyCustom">
      <div class="col-12 col-lg-3 mb-3 mb-lg-0">
        <h3 class="be-policy__label mb-0">More Policies</h3>
      </div>
      <div class="col-12 col-lg-9">
        <div
          v-for="(policy, index) in policyCustomList"
          :key="index"
          class="be-policy__custom-item"
          :class="{ 'mt-4': index > 0 }">
          <h4 class="be-policy__custom-title mb-2">{{ policy.title }}</h4>
          <div class="be-policy__content" v-html="sanitizePolicyHtml(policy.description)"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';

const POLICY_TIME_FORMAT = 'hh:mm A';

export default {
  name: 'BeAccommodationPolicy',
  computed: {
    ...mapGetters(['getMerchant']),
    hasCheckTimes() {
      return this.$isNotEmptyV2(this.getMerchant.check_in_time_start)
        && this.$isNotEmptyV2(this.getMerchant.check_out_time_end);
    },
    checkInTime() {
      return this.formatPolicyTime(this.getMerchant.check_in_time_start);
    },
    checkOutTime() {
      return this.formatPolicyTime(this.getMerchant.check_out_time_end);
    },
    hasCheckInPolicy() {
      return this.$isNotEmptyV2(this.getMerchant.check_in_policy);
    },
    hasCheckInSection() {
      return this.hasCheckTimes || this.hasCheckInPolicy;
    },
    policyCustomList() {
      return this.getMerchant.policy_custom || [];
    },
    hasPolicyCustom() {
      return this.policyCustomList.length > 0;
    },
    hasAnyPolicy() {
      return this.hasCheckInSection || this.hasPolicyCustom;
    },
  },
  methods: {
    formatPolicyTime(time) {
      if (!this.$isNotEmptyV2(time)) return '';
      return moment(time, 'HH:mm:ss').format(POLICY_TIME_FORMAT);
    },
    // backend policy text carries manual "<p><br></p>" spacers on top of paragraph
    // margins, doubling up the gap between sections — strip them and let CSS space it.
    // Heading-only paragraphs (a lone <strong>) get tagged so CSS can keep them close
    // to their own text while adding more room before the next heading.
    sanitizePolicyHtml(html) {
      if (!this.$isNotEmptyV2(html)) return '';
      const withoutSpacers = html.replace(/<p>\s*(<br\s*\/?>\s*)+<\/p>/gi, '');
      return withoutSpacers.replace(
        /<p>(\s*<strong>[\s\S]*?<\/strong>\s*)<\/p>/gi,
        '<p class="be-policy__content-heading">$1</p>'
      );
    },
  },
};
</script>

<style scoped>
.be-policy__title {
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-weight: 600;
  color: #1a1a2e;
  font-size: 22px;
}
.be-policy__section {
  align-items: flex-start;
}
.be-policy__label {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif;
  font-weight: 800;
  color: #1a1a2e;
  font-size: 16px;
}
.be-policy__divider {
  margin-top: 20px;
  margin-bottom: 20px;
  border: none;
  border-top: 1px solid #e8e8e8;
}
.be-policy__time-row {
  display: flex;
  gap: 48px;
  margin-bottom: 20px;
}
.be-policy__time-label {
  color: #999;
  font-size: 14px;
}
.be-policy__time-value {
  color: #1a1a2e;
  font-weight: 700;
  font-size: 16px;
}
.be-policy__custom-title {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif;
  font-weight: 800;
  color: #1a1a2e;
  font-size: 16px;
}
.be-policy__content >>> p {
  color: #555;
  font-size: 15px;
  line-height: 1.6;
  margin: 0 0 18px;
}
.be-policy__content >>> p.be-policy__content-heading {
  margin-bottom: 6px;
}
.be-policy__content >>> p:last-child {
  margin-bottom: 0;
}
.be-policy__content >>> strong {
  color: #1a1a2e;
  font-weight: 800;
  font-size: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeAccommodationPolicy.vue